import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, ZONES, ZONE_DETAILS, MARKETING_ZONES, PROPERTY_TYPES, formatAED } from "../lib/api";
import PropertyCard from "../components/PropertyCard";
import AiSearchPanel from "../components/AiSearchPanel";
import { useT } from "../lib/i18n";
import useSEO from "../lib/useSEO";
import { businessLd, websiteLd, breadcrumbLd, itemListLd } from "../lib/jsonld";

export default function Home() {
  const navigate = useNavigate();
  const { t } = useT();
  const [featured, setFeatured] = React.useState([]);
  const [stats, setStats] = React.useState({ total_properties: 0, available: 0 });
  const [zoneCounts, setZoneCounts] = React.useState([]);
  const [form, setForm] = React.useState({ zone: "", property_type: "", min_size: "", max_price: "" });

  const jsonLd = React.useMemo(() => [
    businessLd(),
    websiteLd(),
    breadcrumbLd([{ name: "Home", url: "/" }]),
    featured.length ? itemListLd(featured) : null,
  ].filter(Boolean), [featured]);

  useSEO({
    title: "WarehousesUAE — Dubai Warehouses, Industrial & Logistics Properties",
    description: "Find warehouses, industrial units, industrial plots, cold storage, showrooms, staff & labour accommodation and logistics facilities for rent in Dubai. JAFZA, DIP, Al Quoz, Ras Al Khor, Dubai South, DIC, Al Warsan & NIP.",
    canonical: window.location.origin + "/",
    image: window.location.origin + "/og-default.png",
    jsonLd,
  });

  React.useEffect(() => {
    api.get("/properties?featured=true&limit=6").then((r) => setFeatured(r.data));
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/zones").then((r) => setZoneCounts(r.data));
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    Object.entries(form).forEach(([k, v]) => v && params.set(k, v));
    navigate(`/properties?${params.toString()}`);
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-zinc-950 text-white overflow-hidden">
        <div className="absolute inset-0 bp-grid-dark opacity-50"></div>
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/36176840/pexels-photo-36176840.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600)" }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/85 to-transparent"></div>
        
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#FFCC00] mb-4">
            {t("hero.kicker")}
          </div>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9] max-w-4xl mb-6">
            {t("hero.title_a")}
            <br /><span className="text-[#FFCC00]">{t("hero.title_b")}</span>
            <br />{t("hero.title_c")}
          </h1>
          <p className="text-zinc-300 text-lg max-w-2xl mb-10 leading-relaxed">
            {t("hero.subtitle")}
          </p>

          {/* Search Panel */}
          <form
            onSubmit={submitSearch}
            data-testid="hero-search-form"
            className="bg-white text-zinc-900 border border-zinc-900 max-w-5xl"
            style={{ boxShadow: "8px 8px 0 0 #031833" }}
          >
            <div className="grid md:grid-cols-5 gap-0">
              <div className="md:col-span-1 p-4 border-b md:border-b-0 md:border-r border-zinc-200">
                <label className="font-mono-tech text-[9px] uppercase text-zinc-500 block mb-1.5">{t("hero.zone")}</label>
                <select
                  data-testid="hero-zone-select"
                  value={form.zone}
                  onChange={(e) => setForm({ ...form, zone: e.target.value })}
                  className="w-full bg-transparent border-none outline-none font-mono-tech text-sm"
                >
                  <option value="">{t("hero.any_zone")}</option>
                  {ZONES.map((z) => <option key={z} value={z}>{z}</option>)}
                </select>
              </div>
              <div className="md:col-span-1 p-4 border-b md:border-b-0 md:border-r border-zinc-200">
                <label className="font-mono-tech text-[9px] uppercase text-zinc-500 block mb-1.5">{t("hero.type")}</label>
                <select
                  data-testid="hero-type-select"
                  value={form.property_type}
                  onChange={(e) => setForm({ ...form, property_type: e.target.value })}
                  className="w-full bg-transparent border-none outline-none font-mono-tech text-sm"
                >
                  <option value="">{t("hero.all_types")}</option>
                  {PROPERTY_TYPES.map((pt) => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
                </select>
              </div>
              <div className="md:col-span-1 p-4 border-b md:border-b-0 md:border-r border-zinc-200">
                <label className="font-mono-tech text-[9px] uppercase text-zinc-500 block mb-1.5">{t("hero.min_size")}</label>
                <input
                  data-testid="hero-min-size"
                  type="number"
                  placeholder="5,000"
                  value={form.min_size}
                  onChange={(e) => setForm({ ...form, min_size: e.target.value })}
                  className="w-full bg-transparent border-none outline-none font-mono-tech text-sm"
                />
              </div>
              <div className="md:col-span-1 p-4 border-b md:border-b-0 md:border-r border-zinc-200">
                <label className="font-mono-tech text-[9px] uppercase text-zinc-500 block mb-1.5">{t("hero.max_price")}</label>
                <input
                  data-testid="hero-max-price"
                  type="number"
                  placeholder="2,000,000"
                  value={form.max_price}
                  onChange={(e) => setForm({ ...form, max_price: e.target.value })}
                  className="w-full bg-transparent border-none outline-none font-mono-tech text-sm"
                />
              </div>
              <button
                data-testid="hero-search-submit"
                type="submit"
                className="md:col-span-1 bg-zinc-900 text-white font-display tracking-tight text-lg hover:bg-[#031833] transition-colors"
              >
                {t("hero.search")}
              </button>
            </div>
          </form>

          {/* Marquee Stats */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10">
            {[
              { label: t("hero.stat_listings"), value: stats.total_properties || "247" },
              { label: t("hero.stat_sqft"), value: "2.4M" },
              { label: t("hero.stat_zones"), value: "12" },
              { label: t("hero.stat_match"), value: "< 48h" },
            ].map((s, i) => (
              <div key={i} className="bg-zinc-950 p-5">
                <div className="font-display text-3xl md:text-4xl tracking-tighter text-[#FFCC00]">{s.value}</div>
                <div className="font-mono-tech text-[10px] uppercase text-zinc-400 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Marquee strip */}
        <div className="diag-stripes h-3"></div>
        <div className="bg-zinc-900 py-3 overflow-hidden border-y border-zinc-800">
          <div className="marquee flex whitespace-nowrap font-mono-tech text-xs uppercase tracking-widest text-zinc-400">
            {[...Array(2)].map((_, k) => (
              <div key={k} className="flex">
                {["JAFZA", "DIP", "AL QUOZ", "RAS AL KHOR", "DUBAI SOUTH", "DIC", "WAREHOUSE", "COLD STORAGE", "LOGISTICS", "INDUSTRIAL", "SHOWROOM"].map((tag) => (
                  <span key={`${k}-${tag}`} className="px-8 flex items-center gap-2">
                    <span className="w-1 h-1 bg-[#FFCC00]"></span>{tag}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="py-20 bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-2">{t("home.featured_kicker")}</div>
              <h2 className="font-display text-4xl md:text-5xl tracking-tighter">{t("home.featured_title")}</h2>
            </div>
            <Link to="/properties" data-testid="home-view-all-btn" className="hidden md:inline-flex font-mono-tech text-xs uppercase border-b-2 border-zinc-900 pb-1 hover:text-[#031833] hover:border-[#031833]">
              {t("home.view_all")}
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {featured.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* AI Search */}
      <AiSearchPanel />

      {/* Zones grid */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-2">{t("home.zones_kicker")}</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tighter mb-10 max-w-3xl">
            {t("home.zones_title_a")}<br />{t("home.zones_title_b")}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {MARKETING_ZONES.slice(0, 9).map((name) => {
              const z = ZONE_DETAILS[name];
              const count = zoneCounts.find((zc) => zc.name === name)?.count;
              return (
                <Link
                  key={name}
                  to={`/zones/${z.slug}`}
                  data-testid={`zone-card-${name}`}
                  className="group relative aspect-[4/3] overflow-hidden bg-zinc-900"
                >
                  <img src={z.img} alt={name} className="w-full h-full object-cover opacity-60 group-hover:opacity-50 group-hover:scale-105 transition-all duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent"></div>
                  <div className="absolute inset-0 p-6 flex flex-col justify-between">
                    <div className="font-mono-tech text-[10px] uppercase text-white/70">
                      {count ? `${count} listings` : ""}
                    </div>
                    <div>
                      <h3 className="font-display text-3xl md:text-4xl tracking-tighter text-white mb-1">{name}</h3>
                      <p className="text-white/80 text-sm">{z.tagline}</p>
                      <div className="mt-3 font-mono-tech text-[10px] uppercase text-[#FFCC00]">EXPLORE →</div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Categories grid */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-2">{t("home.categories_kicker")}</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tighter mb-10 max-w-3xl">
            {t("home.categories_title_a")}<br />{t("home.categories_title_b")}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { type: "warehouse",            label: t("cat.warehouse"),            img: "https://images.unsplash.com/photo-1601599963565-b7f49deb3d50?w=900&q=80" },
              { type: "industrial_unit",      label: t("cat.industrial_unit"),      img: "https://images.unsplash.com/photo-1565891741441-64926e441838?w=900&q=80" },
              { type: "industrial_plot",      label: t("cat.industrial_plot"),      img: "https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=900&q=80" },
              { type: "showroom",             label: t("cat.showroom"),             img: "https://images.unsplash.com/photo-1604754742629-3e0498a26df3?w=900&q=80" },
              { type: "logistics",            label: t("cat.logistics"),            img: "https://images.pexels.com/photos/4487383/pexels-photo-4487383.jpeg?w=900" },
              { type: "storage",              label: t("cat.storage"),              img: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=900&q=80" },
              { type: "staff_accommodation",  label: t("cat.staff_accommodation"),  img: "https://customer-assets.emergentagent.com/job_dubai-storage-spaces/artifacts/3jsyo5c3_Screenshot%202026-06-09%20at%202.28.03%E2%80%AFPM.png" },
            ].map((c, idx) => (
              <Link
                key={c.type}
                to={`/properties?property_type=${c.type}`}
                data-testid={`category-card-${c.type}`}
                className="group relative aspect-[4/5] overflow-hidden bg-zinc-900"
              >
                <img src={c.img} alt={c.label} className="w-full h-full object-cover opacity-65 group-hover:opacity-50 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/90 via-zinc-950/20 to-transparent"></div>
                <div className="absolute inset-0 p-4 md:p-5 flex flex-col justify-between">
                  <div className="font-mono-tech text-[10px] uppercase text-[#FFCC00] tracking-wider">
                    0{idx + 1} //
                  </div>
                  <div>
                    <h3 className="font-display text-xl md:text-2xl tracking-tighter text-white leading-tight mb-1">{c.label}</h3>
                    <div className="font-mono-tech text-[10px] uppercase text-white/80 group-hover:text-[#FFCC00] transition-colors">
                      BROWSE →
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Services band */}
      <section className="py-20 bg-zinc-950 text-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="font-mono-tech text-[10px] uppercase text-[#FFCC00] mb-3">{t("home.why_kicker")}</div>
            <h2 className="font-display text-5xl md:text-6xl tracking-tighter mb-6">
              {t("home.why_title_a")}<br />{t("home.why_title_b")}<br />{t("home.why_title_c")}
            </h2>
            <p className="text-zinc-400 max-w-md leading-relaxed">
              {t("home.why_sub")}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-px bg-white/10">
            {[
              { n: "01", title: t("home.principle_1_t"), d: t("home.principle_1_d") },
              { n: "02", title: t("home.principle_2_t"), d: t("home.principle_2_d") },
              { n: "03", title: t("home.principle_3_t"), d: t("home.principle_3_d") },
              { n: "04", title: t("home.principle_4_t"), d: t("home.principle_4_d") },
            ].map((s) => (
              <div key={s.n} className="bg-zinc-950 p-6">
                <div className="font-mono-tech text-[10px] text-[#031833] mb-3">{s.n} //</div>
                <h3 className="font-display text-xl tracking-tighter mb-2">{s.title}</h3>
                <p className="text-sm text-zinc-400">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#FFCC00]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <div className="font-mono-tech text-[10px] uppercase mb-3">{t("home.cta_kicker")}</div>
            <h2 className="font-display text-5xl md:text-6xl tracking-tighter leading-[0.95]">
              {t("home.cta_title_a")}
              <br />{t("home.cta_title_b")}
            </h2>
          </div>
          <div className="md:col-span-5 flex flex-col gap-3">
            <Link
              to="/contact"
              data-testid="cta-contact-btn"
              className="block bg-zinc-950 text-white px-6 py-5 font-display text-2xl tracking-tighter hover:bg-[#031833]"
            >
              {t("home.cta_request")}
            </Link>
            <a
              href="https://wa.me/971588320669"
              data-testid="cta-whatsapp-btn"
              className="block bg-white text-zinc-950 px-6 py-5 font-display text-2xl tracking-tighter border border-zinc-950 hover:bg-zinc-950 hover:text-white"
            >
              {t("home.cta_whatsapp")}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
